import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import {
  api,
  ApiError,
  type Expense,
  type ExpensesResponse,
  type Participant,
  type Settlement,
  type SpaceDetail,
} from '../../lib/api'
import { TabBar } from '../../components/TabBar'
import { invaliderDepenses } from '../../lib/cache'
import { CategoryMark } from '../spaces/CategoryMark'
import { layout, sousLaBarre } from '../../design/tokens'

/** Centimes entiers vers euros lisibles. Aucun flottant ne traverse ce calcul. */
function euros(centimes: number) {
  const signe = centimes < 0 ? '-' : ''
  const absolu = Math.abs(centimes)

  return `${signe}${Math.floor(absolu / 100)},${String(absolu % 100).padStart(2, '0')} €`
}

/**
 * Dépenses partagées — planche 15 : « solde en une phrase, parts au curseur ».
 *
 * La phrase est en tête et en grand : c'est la seule chose qu'on vient vraiment lire. Le détail
 * des dépenses est là pour la justifier, pas pour être épluché.
 */
export function ExpensesScreen() {
  const { spaceId = '' } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  // Une seule feuille de saisie, qui sert à créer comme à corriger : deux formulaires jumeaux
  // finiraient par diverger, et c'est le genre d'écart qui se voit sur un montant.
  const [parametres] = useSearchParams()
  const [saisie, setSaisie] = useState<{ depense: Expense | null } | null>(
    parametres.get('ajout') === '1' ? { depense: null } : null,
  )
  const [remboursement, setRemboursement] = useState(false)
  const [gestion, setGestion] = useState(false)
  const [message, setMessage] = useState<string | null>(null)

  const { data: espace } = useQuery<SpaceDetail>({
    queryKey: ['espace', spaceId],
    queryFn: () => api.space(spaceId),
  })

  const { data, isPending } = useQuery<ExpensesResponse>({
    queryKey: ['depenses', spaceId],
    queryFn: () => api.expenses(spaceId),
  })

  const rafraichir = () => invaliderDepenses(queryClient, spaceId)

  const regler = useMutation({
    mutationFn: () => api.settleExpenses(spaceId),
    onSuccess: async (resultat) => {
      setMessage(resultat.message)
      await rafraichir()
    },
    onError: (cause: unknown) =>
      setMessage(cause instanceof ApiError ? cause.message : 'Le règlement a échoué.'),
  })

  return (
    <main
      className="mx-auto min-h-dvh bg-surface"
      style={{
        maxWidth: layout.column,
        paddingInline: layout.gutter,
        paddingBottom: sousLaBarre(32),
      }}
    >
      <header className="flex items-center gap-3" style={{ height: layout.header }}>
        <button
          type="button"
          onClick={() => void navigate(-1)}
          aria-label="Retour"
          className="flex shrink-0 items-center justify-center rounded-field border border-border bg-card text-card-title font-semibold"
          style={{ width: 40, height: 40 }}
        >
          ←
        </button>
        <h1 className="grow truncate font-serif text-screen">Dépenses</h1>
      </header>

      {/* Le solde en une phrase, en tête et en grand. Un tableau de soldes croisés dit la même
          chose et ne se lit pas — c'est la consigne de la planche. */}
      <section className="rounded-card border border-border bg-card p-4">
        <p className="font-serif text-record">{data?.balance.sentence ?? '…'}</p>

        {/* Qui verse à qui. La phrase au-dessus dit où l'on en est ; ces lignes disent quoi
            faire. À deux elles répètent la phrase, et l'on ne les affiche donc pas : c'est au
            delà que le solde cesse de se résoudre d'un seul virement. */}
        {data && data.balance.transfers.length > 1 ? (
          <ul className="mt-4 flex flex-col gap-2 border-t border-border pt-3">
            {data.balance.transfers.map((virement) => (
              <li
                key={`${virement.fromParticipantId}-${virement.toParticipantId}`}
                className="flex items-baseline justify-between gap-3"
              >
                <span className="truncate text-body">
                  {virement.fromName} <span className="text-ink-muted">→</span> {virement.toName}
                </span>
                <span className="shrink-0 text-card-title font-semibold">
                  {euros(virement.amountCents)}
                </span>
              </li>
            ))}
          </ul>
        ) : null}

        {data && data.balance.amountCents > 0 ? (
          <button
            type="button"
            disabled={regler.isPending}
            onClick={() => regler.mutate()}
            className="mt-4 w-full rounded-button bg-accent text-card-title font-semibold text-card disabled:opacity-60"
            style={{ height: layout.primaryButtonHeight }}
          >
            {regler.isPending ? 'Règlement…' : 'Marquer comme réglé'}
          </button>
        ) : null}

        {data && data.balance.amountCents > 0 ? (
          <p className="mt-2 text-meta text-ink-muted">
            Les dépenses sont archivées, pas effacées : le solde repart de zéro.
          </p>
        ) : null}

        {/* Ce que chacun a avancé et ce qui lui revient : la phrase dit le solde, ces deux
            lignes disent d'où il vient. C'est ce qui manquait le plus pour se fier au chiffre. */}
        {data && data.expenses.length > 0 ? (
          <div className="mt-4 border-t border-border pt-3">
            <p className="text-meta text-ink-muted">
              {euros(data.totalCents)} dépensés en tout
            </p>
            <ul className="mt-2 flex flex-col gap-1">
              {data.balance.lines.map((ligne) => (
                <li key={ligne.participantId} className="flex items-baseline justify-between gap-3">
                  <span className="truncate text-body">{ligne.displayName}</span>
                  <span className="shrink-0 text-meta text-ink-muted">
                    a avancé {euros(ligne.paidCents)} · à sa charge {euros(ligne.owedCents)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {data && data.participants.length > 0 ? (
          <div className="mt-3">
            <button
              type="button"
              onClick={() => setGestion((ouvert) => !ouvert)}
              className="text-meta font-semibold text-ink-muted"
            >
              {data.participants.map((participant) => participant.displayName).join(', ')} ·{' '}
              {gestion ? 'fermer' : 'gérer'}
            </button>

            {gestion ? (
              <ul className="mt-2 flex flex-col gap-2 border-t border-border pt-2">
                {data.participants.map((participant) => (
                  <li key={participant.id} className="flex items-center justify-between gap-3">
                    <span className="grow truncate text-body">
                      {participant.displayName}
                      <span className="text-meta text-ink-muted">
                        {participant.isGuest ? ' · invité' : ' · membre'}
                        {participant.isRemoved ? ' · écarté' : ''}
                      </span>
                    </span>

                    {/* Un membre ne s'écarte pas d'ici : il se retire de l'espace. */}
                    {participant.isGuest ? (
                      <button
                        type="button"
                        onClick={async () => {
                          await api.patchParticipant(spaceId, participant.id, {
                            removed: !participant.isRemoved,
                          })
                          await rafraichir()
                        }}
                        className="shrink-0 text-meta font-semibold text-ink-muted"
                      >
                        {participant.isRemoved ? 'Réintégrer' : 'Écarter'}
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        {data && data.balance.amountCents > 0 && data.balance.fromParticipantId && data.balance.toParticipantId ? (
          <button
            type="button"
            onClick={() => setRemboursement((ouvert) => !ouvert)}
            className="mt-3 w-full rounded-button border border-border bg-card text-card-title font-semibold"
            style={{ height: layout.primaryButtonHeight }}
          >
            {remboursement ? 'Annuler' : 'Enregistrer un remboursement'}
          </button>
        ) : null}

        {remboursement && data?.balance.fromParticipantId && data.balance.toParticipantId ? (
          <Remboursement
            spaceId={spaceId}
            de={data.balance.fromParticipantId}
            vers={data.balance.toParticipantId}
            maximum={data.balance.amountCents}
            onDone={async () => {
              setRemboursement(false)
              await rafraichir()
            }}
          />
        ) : null}

        {message ? <p className="mt-3 text-meta text-accent">{message}</p> : null}
      </section>

      {saisie ? (
        <Formulaire
          spaceId={spaceId}
          participants={data?.participants ?? []}
          categories={espace?.categories ?? []}
          depense={saisie.depense}
          onDone={async () => {
            setSaisie(null)
            await rafraichir()
          }}
          onCancel={() => setSaisie(null)}
        />
      ) : null}

      {isPending ? (
        <p className="mt-4 text-body text-ink-muted">Chargement…</p>
      ) : data && data.expenses.length > 0 ? (
        <ul className="mt-4 flex flex-col gap-2">
          {data.expenses.map((depense) => (
            <li
              key={depense.id}
              className="flex items-center gap-3 rounded-card border border-border bg-card p-3"
            >
              {depense.categoryInitials && depense.categoryHue !== null ? (
                <CategoryMark
                  initials={depense.categoryInitials}
                  hue={depense.categoryHue}
                  size="small"
                />
              ) : null}

              {/* Toucher la ligne pour la corriger : jusqu'ici il fallait la supprimer et
                  tout ressaisir pour un chiffre de travers. */}
              <button
                type="button"
                onClick={() => (depense.isRepayment ? undefined : setSaisie({ depense }))}
                disabled={depense.isRepayment}
                className="grow truncate text-left"
              >
                <span className="block truncate text-card-title font-semibold">
                  {depense.isRepayment ? '↩ ' : ''}
                  {depense.label}
                </span>
                <span className="block truncate text-meta text-ink-muted">
                  {depense.paidByName} · {new Date(depense.occurredOn).toLocaleDateString('fr-FR')}
                  {depense.splitMode === 'equal' || depense.isRepayment ? '' : ' · parts inégales'}
                  {depense.itemTitle ? ` · ${depense.itemTitle}` : ''}
                </span>
              </button>

              <span className="shrink-0 text-card-title font-semibold">
                {euros(depense.amountCents)}
              </span>

              <button
                type="button"
                onClick={async () => {
                  await api.deleteExpense(spaceId, depense.id)
                  await rafraichir()
                }}
                aria-label={`Retirer ${depense.label}`}
                className="shrink-0 text-meta font-semibold text-ink-muted"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <div style={{ paddingBlock: 32 }}>
          <p className="text-body">Aucune dépense en cours.</p>
          <p className="mt-2 text-body text-ink-muted">
            Ajoutez ce que l'un a payé pour deux : le solde se calcule tout seul.
          </p>
        </div>
      )}

      <Historique spaceId={spaceId} />

      <TabBar onAdd={() => setSaisie({ depense: null })} activeSpaceId={spaceId} />
    </main>
  )
}

/**
 * Les périodes déjà réglées.
 *
 * « Marquer comme réglé » archive les dépenses au lieu de les effacer, et l'application le
 * promettait depuis le début — sans jamais offrir d'endroit où les relire. Replié par défaut :
 * on vient sur cet écran pour le solde en cours, l'histoire ancienne attend qu'on la demande, et
 * elle n'est chargée qu'à ce moment-là.
 */
function Historique({ spaceId }: { spaceId: string }) {
  const [ouvert, setOuvert] = useState(false)
  const [deplie, setDeplie] = useState<string | null>(null)

  const { data, isPending } = useQuery<readonly Settlement[]>({
    queryKey: ['reglements', spaceId],
    queryFn: () => api.settlements(spaceId),
    enabled: ouvert,
  })

  return (
    <section className="mt-6">
      <button
        type="button"
        onClick={() => setOuvert((etat) => !etat)}
        className="w-full rounded-button border border-border bg-card text-card-title font-semibold"
        style={{ height: layout.primaryButtonHeight }}
      >
        {ouvert ? 'Masquer les périodes réglées' : 'Voir les périodes réglées'}
      </button>

      {ouvert && isPending ? (
        <p className="mt-3 text-body text-ink-muted">Chargement…</p>
      ) : null}

      {ouvert && data && data.length === 0 ? (
        <p className="mt-3 text-body text-ink-muted">
          Aucune période réglée pour l'instant.
        </p>
      ) : null}

      {ouvert && data && data.length > 0 ? (
        <ul className="mt-3 flex flex-col gap-2">
          {data.map((periode) => (
            <li key={periode.id} className="rounded-card border border-border bg-card p-4">
              <button
                type="button"
                onClick={() => setDeplie((actuel) => (actuel === periode.id ? null : periode.id))}
                className="w-full text-left"
              >
                <span className="block text-card-title font-semibold">{periode.sentence}</span>
                <span className="mt-1 block text-meta text-ink-muted">
                  {new Date(periode.settledAt).toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                  {' · '}
                  {periode.expenseCount} dépense{periode.expenseCount > 1 ? 's' : ''}
                  {' · '}
                  {euros(periode.totalCents)} en tout
                </span>
              </button>

              {deplie === periode.id ? (
                <ul className="mt-3 flex flex-col gap-2 border-t border-border pt-3">
                  {periode.expenses.map((depense) => (
                    <li key={depense.id} className="flex items-baseline justify-between gap-3">
                      <span className="grow truncate text-meta">
                        {depense.isRepayment ? '↩ ' : ''}
                        {depense.label}
                        <span className="text-ink-muted"> · {depense.paidByName}</span>
                      </span>
                      <span className="shrink-0 text-card-title font-semibold">
                        {euros(depense.amountCents)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  )
}

/**
 * Les parts d'une dépense existante, relues en pourcentages.
 *
 * Depuis les montants et non les poids : les poids ne sortent pas de l'API, et les montants
 * disent la même chose — 70/30 en euros redonne 70/30 en parts.
 */
function partsRelues(depense: Expense): Record<string, number> {
  if (depense.amountCents <= 0) {
    return {}
  }

  return Object.fromEntries(
    depense.shares.map((part) => [
      part.participantId,
      Math.max(1, Math.round((part.amountCents / depense.amountCents) * 100)),
    ]),
  )
}

function Formulaire({
  spaceId,
  participants,
  categories,
  depense,
  onDone,
  onCancel,
}: {
  spaceId: string
  participants: readonly Participant[]
  categories: readonly { readonly id: string; readonly name: string }[]
  /** Non nulle : on corrige cette dépense au lieu d'en créer une. */
  depense: Expense | null
  onDone: () => Promise<void>
  onCancel: () => void
}) {
  const queryClient = useQueryClient()

  // Qui partage, par défaut : les membres de l'espace. Les invités attendent d'être désignés —
  // un espace dure, et l'ami d'un soir n'a pas à payer les courses du mois suivant.
  const parDefaut = participants
    .filter((participant) => !participant.isGuest && !participant.isRemoved)
    .map((participant) => participant.id)

  const [libelle, setLibelle] = useState(depense?.label ?? '')
  const [montant, setMontant] = useState(
    depense ? String(depense.amountCents / 100).replace('.', ',') : '',
  )
  const [payeur, setPayeur] = useState(
    depense?.paidByParticipantId ?? parDefaut[0] ?? participants[0]?.id ?? '',
  )
  const [categorie, setCategorie] = useState(depense?.categoryId ?? '')
  const [date, setDate] = useState(depense?.occurredOn ?? new Date().toISOString().slice(0, 10))
  const [qui, setQui] = useState<string[]>(
    depense ? depense.shares.map((part) => part.participantId) : parDefaut,
  )
  const [poids, setPoids] = useState<Record<string, number>>(
    depense ? partsRelues(depense) : {},
  )
  const [inegal, setInegal] = useState(depense ? depense.splitMode !== 'equal' : false)
  const [invitation, setInvitation] = useState<string | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)

  const poidsDe = (participantId: string) => poids[participantId] ?? 1

  const inviter = useMutation({
    mutationFn: (nom: string) => api.inviteParticipant(spaceId, nom),
    onSuccess: async (invite) => {
      setInvitation(null)
      // Ajouté au partage dans la foulée : on n'invite quelqu'un que parce qu'il était là.
      setQui((actuels) => [...actuels, invite.id])
      await invaliderDepenses(queryClient, spaceId)
    },
    onError: (cause: unknown) =>
      setErreur(cause instanceof ApiError ? cause.message : "L'invitation a échoué."),
  })

  const creer = useMutation({
    mutationFn: () => {
      const centimes = Math.round(Number(montant.replace(',', '.')) * 100)

      const corps = {
        label: libelle.trim(),
        amountCents: centimes,
        ...(payeur ? { paidByParticipantId: payeur } : {}),
        ...(categorie ? { categoryId: categorie } : {}),
        ...(date ? { occurredOn: date } : {}),
        // La fiche d'origine est reconduite telle quelle : c'est elle qui porte le secret d'un
        // cadeau, la perdre en corrigeant un montant dévoilerait le prix.
        ...(depense?.itemId ? { itemId: depense.itemId } : {}),
        ...(inegal
          ? {
              splitMode: 'shares' as const,
              shares: qui.map((participantId) => ({ participantId, weight: poidsDe(participantId) })),
            }
          : { shares: qui.map((participantId) => ({ participantId })) }),
      }

      return depense
        ? api.updateExpense(spaceId, depense.id, corps)
        : api.createExpense(spaceId, corps)
    },
    onSuccess: onDone,
    onError: (cause: unknown) =>
      setErreur(cause instanceof ApiError ? cause.message : "L'ajout a échoué."),
  })

  const montantValide = Number(montant.replace(',', '.')) > 0
  const partageables = participants.filter(
    (participant) => !participant.isRemoved || qui.includes(participant.id),
  )

  return (
    <section className="mt-4 rounded-card border border-border bg-card p-4">
      <label className="block text-label font-semibold uppercase text-ink-muted" htmlFor="libelle">
        Quoi
      </label>
      <input
        id="libelle"
        autoFocus
        value={libelle}
        onChange={(evenement) => setLibelle(evenement.target.value)}
        placeholder="Courses de la semaine"
        className="mt-2 w-full rounded-field border-[1.5px] border-accent bg-card px-4 outline-none"
        style={{ height: 56, fontSize: 17 }}
      />

      <div className="mt-4 flex gap-3">
        <div className="grow">
          <label className="block text-label font-semibold uppercase text-ink-muted" htmlFor="montant">
            Combien
          </label>
          <input
            id="montant"
            inputMode="decimal"
            value={montant}
            onChange={(evenement) => {
              setMontant(evenement.target.value)
              setErreur(null)
            }}
            placeholder="42,50"
            className="mt-2 w-full rounded-field border border-border bg-card px-4 outline-none"
            style={{ height: 56, fontSize: 17 }}
          />
        </div>
        <div className="grow">
          <label className="block text-label font-semibold uppercase text-ink-muted" htmlFor="date-depense">
            Quand
          </label>
          <input
            id="date-depense"
            type="date"
            value={date}
            onChange={(evenement) => setDate(evenement.target.value)}
            className="mt-2 w-full rounded-field border border-border bg-card px-4 outline-none"
            style={{ height: 56, fontSize: 17 }}
          />
        </div>
      </div>

      <label className="mt-4 block text-label font-semibold uppercase text-ink-muted" htmlFor="payeur">
        Payé par
      </label>
      <select
        id="payeur"
        value={payeur}
        onChange={(evenement) => setPayeur(evenement.target.value)}
        className="mt-2 w-full rounded-field border border-border bg-card px-4 outline-none"
        style={{ height: 56, fontSize: 17 }}
      >
        {partageables.map((participant) => (
          <option key={participant.id} value={participant.id}>
            {participant.displayName}
          </option>
        ))}
      </select>

      <label className="mt-4 block text-label font-semibold uppercase text-ink-muted" htmlFor="categorie-depense">
        Catégorie (facultative)
      </label>
      <select
        id="categorie-depense"
        value={categorie}
        onChange={(evenement) => setCategorie(evenement.target.value)}
        className="mt-2 w-full rounded-field border border-border bg-card px-4 outline-none"
        style={{ height: 56, fontSize: 17 }}
      >
        <option value="">Aucune</option>
        {categories.map((category) => (
          <option key={category.id} value={category.id}>
            {category.name}
          </option>
        ))}
      </select>

      {/* Qui partage. Un invité s'ajoute ici même : on ne pense à lui qu'au moment de saisir
          l'addition où il était. */}
      <p className="mt-4 text-label font-semibold uppercase text-ink-muted">Partagé entre</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {partageables.map((participant) => {
          const pris = qui.includes(participant.id)

          return (
            <button
              key={participant.id}
              type="button"
              aria-pressed={pris}
              onClick={() =>
                setQui((actuels) =>
                  pris
                    ? actuels.filter((identifiant) => identifiant !== participant.id)
                    : [...actuels, participant.id],
                )
              }
              className={`rounded-field border px-3 text-body ${
                pris ? 'border-accent bg-accent-soft font-semibold' : 'border-border text-ink-muted'
              }`}
              style={{ height: 40 }}
            >
              {participant.displayName}
            </button>
          )
        })}

        <button
          type="button"
          onClick={() => setInvitation('')}
          className="rounded-field border border-dashed border-border px-3 text-body text-ink-muted"
          style={{ height: 40 }}
        >
          + quelqu'un
        </button>
      </div>

      {invitation !== null ? (
        <div className="mt-3 flex gap-2">
          <input
            autoFocus
            value={invitation}
            onChange={(evenement) => setInvitation(evenement.target.value)}
            placeholder="Son prénom"
            className="grow rounded-field border border-border bg-card px-4 outline-none"
            style={{ height: 48, fontSize: 17 }}
          />
          <button
            type="button"
            disabled={invitation.trim().length === 0 || inviter.isPending}
            onClick={() => inviter.mutate(invitation.trim())}
            className="shrink-0 rounded-button bg-accent px-4 text-card-title font-semibold text-card disabled:opacity-60"
            style={{ height: 48 }}
          >
            Ajouter
          </button>
        </div>
      ) : null}

      <p className="mt-2 text-meta text-ink-muted">
        Un invité n'a pas de compte : il compte dans le partage, il ne voit rien de l'espace.
      </p>

      {/* « Parts au curseur » — planche 15 — quand ils sont deux, où un seul curseur suffit à
          dire qui prend quoi. À trois et plus il ne le peut pas : chacun reçoit alors son propre
          nombre de parts. */}
      {qui.length >= 2 ? (
        <>
          <label className="mt-4 flex items-center gap-3">
            <input
              type="checkbox"
              checked={inegal}
              onChange={(evenement) => setInegal(evenement.target.checked)}
              style={{ width: 20, height: 20 }}
            />
            <span className="text-body">Partager autrement qu'en parts égales</span>
          </label>

          {inegal && qui.length === 2 ? (
            <>
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={poidsDe(qui[0]!)}
                onChange={(evenement) =>
                  setPoids({
                    [qui[0]!]: Number(evenement.target.value),
                    [qui[1]!]: 100 - Number(evenement.target.value),
                  })
                }
                aria-label="Répartition entre les deux personnes"
                className="mt-3 w-full"
              />
              <p className="mt-1 text-meta text-ink-muted">
                {participants.find((participant) => participant.id === qui[0])?.displayName}{' '}
                {poidsDe(qui[0]!)} %{' · '}
                {participants.find((participant) => participant.id === qui[1])?.displayName}{' '}
                {100 - poidsDe(qui[0]!)} %
              </p>
            </>
          ) : null}

          {inegal && qui.length > 2 ? (
            <ul className="mt-3 flex flex-col gap-2">
              {qui.map((participantId) => (
                <li key={participantId} className="flex items-center justify-between gap-3">
                  <span className="grow truncate text-body">
                    {participants.find((participant) => participant.id === participantId)?.displayName}
                  </span>
                  <input
                    type="number"
                    min={1}
                    value={poidsDe(participantId)}
                    onChange={(evenement) =>
                      setPoids((actuels) => ({
                        ...actuels,
                        [participantId]: Math.max(1, Number(evenement.target.value)),
                      }))
                    }
                    aria-label={`Parts de ${
                      participants.find((participant) => participant.id === participantId)?.displayName
                    }`}
                    className="w-20 shrink-0 rounded-field border border-border bg-card px-3 text-center outline-none"
                    style={{ height: 44, fontSize: 17 }}
                  />
                </li>
              ))}
            </ul>
          ) : null}
        </>
      ) : null}

      {erreur ? <p className="mt-3 text-meta text-danger">{erreur}</p> : null}

      <button
        type="button"
        disabled={
          libelle.trim().length === 0 || !montantValide || qui.length === 0 || creer.isPending
        }
        onClick={() => creer.mutate()}
        className="mt-4 w-full rounded-button bg-accent text-card-title font-semibold text-card disabled:opacity-60"
        style={{ height: layout.primaryButtonHeight }}
      >
        {creer.isPending
          ? 'Enregistrement…'
          : depense
            ? 'Enregistrer la correction'
            : 'Ajouter'}
      </button>

      <button
        type="button"
        onClick={onCancel}
        className="mt-2 w-full text-meta font-semibold text-ink-muted"
        style={{ height: 40 }}
      >
        Annuler
      </button>
    </section>
  )
}

/**
 * « J'ai rendu 30 € » — un remboursement partiel.
 *
 * Le sens est prérempli depuis le solde : celui qui doit rend à celui à qui on doit. C'est le
 * seul cas courant, et le demander serait une question dont on connaît déjà la réponse.
 */
function Remboursement({
  spaceId,
  de,
  vers,
  maximum,
  onDone,
}: {
  spaceId: string
  de: string
  vers: string
  maximum: number
  onDone: () => Promise<void>
}) {
  const [montant, setMontant] = useState(String(maximum / 100).replace('.', ','))
  const [erreur, setErreur] = useState<string | null>(null)

  const rendre = useMutation({
    mutationFn: () =>
      api.repayExpense(spaceId, {
        fromParticipantId: de,
        toParticipantId: vers,
        amountCents: Math.round(Number(montant.replace(',', '.')) * 100),
      }),
    onSuccess: onDone,
    onError: (cause: unknown) =>
      setErreur(cause instanceof ApiError ? cause.message : 'Le remboursement a échoué.'),
  })

  const valide = Number(montant.replace(',', '.')) > 0

  return (
    <div className="mt-3 rounded-field border border-border p-3">
      <label className="block text-label font-semibold uppercase text-ink-muted" htmlFor="rendu">
        Montant rendu
      </label>
      <input
        id="rendu"
        autoFocus
        inputMode="decimal"
        value={montant}
        onChange={(evenement) => {
          setMontant(evenement.target.value)
          setErreur(null)
        }}
        className="mt-2 w-full rounded-field border border-border bg-card px-4 outline-none"
        style={{ height: 56, fontSize: 17 }}
      />

      <p className="mt-2 text-meta text-ink-muted">
        Le solde diminue d'autant. Les dépenses restent en place.
      </p>

      {erreur ? <p className="mt-2 text-meta text-danger">{erreur}</p> : null}

      <button
        type="button"
        disabled={!valide || rendre.isPending}
        onClick={() => rendre.mutate()}
        className="mt-3 w-full rounded-button bg-accent text-card-title font-semibold text-card disabled:opacity-60"
        style={{ height: layout.primaryButtonHeight }}
      >
        {rendre.isPending ? 'Enregistrement…' : "C'est rendu"}
      </button>
    </div>
  )
}
